import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  Upload, 
  X, 
  Download, 
  AlertCircle,
  AlertTriangle,
  Loader2,
  FileText,
  PackageCheck,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function AddMasivoModal({ userId, user, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  function onDrop(acceptedFiles) {
    setFile(acceptedFiles[0]);
    setResult(null);
    setError(null);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  function clearFile() {
    setFile(null);
    setResult(null);
    setError(null);
  }

  async function handleUpload() {
    if (!file) {
      setError('Por favor selecciona un archivo primero.');
      return;
    }
    
    setUploading(true);
    setResult(null);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/delivery_points/bulk_upload`,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          } 
        }
      );
      
      setResult(response.data);
      setShowSuccessModal(true);
      
      // Auto-close success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        if (onSuccess) onSuccess();
      }, 3000);
      
    } catch (err) {
      console.error('Error uploading bulk file:', err);
      setError(err.response?.data?.error || 'Error al subir el archivo.');
    } finally {
      setUploading(false);
    }
  }

  function downloadTemplate() {
    const headers = [
      'Nombre',
      'Direccion',
      'WhatsApp',
      'Distrito',
      'Fecha',
      'Productos',
      'Cargo',
      'Notas'
    ];
    
    const exampleRow = [
      'Juan Pérez',
      'Av. Principal 123',
      '987654321',
      'San Isidro',
      '15/01/2025',
      '2x Producto A, 1x Producto B',
      '15.50',
      'Entregar en horario de oficina'
    ];

    const csvContent = [
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `plantilla_carga_masiva_${user?.username || userId}.csv`;
    link.click();
  }

  function renderDetailsList(items, fields) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
        {items.map((item, index) => (
          <div 
            key={index} 
            className="bg-white rounded-lg p-3 border border-slate-200"
          >
            <p className="text-sm font-medium text-slate-900 font-montserrat">
              {fields.map(k => item[k]).filter(Boolean).join(' • ')}
            </p>
            {(item.reason || item.error) && (
              <p className="text-xs text-slate-600 mt-1 font-montserrat line-clamp-2">
                {(item.reason || item.error).toString().slice(0, 200)}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm" 
        onClick={onClose} 
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[90dvh] min-h-0 overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-3 py-3 sm:px-6 sm:py-5 border-b border-slate-200 bg-gradient-to-r from-[#67A43C]/5 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#67A43C]/10 rounded-lg">
                  <Upload className="w-6 h-6 text-[#67A43C]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 font-montserrat">
                    Carga Masiva de Entregas
                  </h3>
                  <p className="text-sm text-slate-500 font-montserrat">
                    Para: <span className="font-medium text-slate-700">{user?.username || `Usuario #${userId}`}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={uploading}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-6 sm:py-5">
            <div className="space-y-5">
              
              {/* Instructions Card */}
              <div className="bg-gradient-to-r from-[#67A43C]/10 to-[#E6B43C]/10 border-l-4 border-[#67A43C] rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#67A43C] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-[#67A43C] font-montserrat mb-2">
                      Instrucciones
                    </h4>
                    <div className="text-xs text-slate-700 font-montserrat space-y-1">
                      <p>• <strong>Campos obligatorios:</strong> Nombre, dirección, WhatsApp, distrito y fecha</p>
                      <p>• <strong>Formato de fecha:</strong> DD/MM/AAAA (ej: 15/01/2025)</p>
                      <p>• <strong>WhatsApp:</strong> 9 dígitos sin código de país (ej: 987654321)</p>
                      <p>• <strong>Productos:</strong> Formato "2x Producto A, 1x Producto B"</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`
                  relative bg-white border-2 border-dashed rounded-2xl p-8 
                  text-center cursor-pointer transition-all duration-200
                  ${isDragActive 
                    ? 'border-[#67A43C] bg-[#67A43C]/5 scale-[1.02]' 
                    : 'border-slate-300 hover:border-[#67A43C] hover:bg-slate-50'
                  }
                  ${uploading ? 'pointer-events-none opacity-50' : ''}
                `}
              >
                <input {...getInputProps()} disabled={uploading} />
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#67A43C] to-[#E6B43C] flex items-center justify-center">
                    <Upload className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-slate-900 font-montserrat mb-1">
                      {isDragActive 
                        ? '¡Suelta el archivo aquí!' 
                        : 'Arrastra un archivo o haz clic para seleccionar'
                      }
                    </p>
                    <p className="text-sm text-slate-600 font-montserrat">
                      Archivos Excel (.xlsx, .xls)
                    </p>
                  </div>
                </div>
              </div>

              {/* File Preview */}
              {file && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-[#67A43C]/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#67A43C]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate font-montserrat">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-600 font-montserrat">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFile();
                      }}
                      disabled={uploading}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200 flex-shrink-0 disabled:opacity-50"
                      aria-label="Quitar archivo"
                    >
                      <X className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={downloadTemplate}
                  disabled={uploading}
                  className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm font-montserrat disabled:opacity-50"
                >
                  <Download className="w-5 h-5" />
                  <span>Descargar plantilla</span>
                </button>

                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex items-center justify-center gap-2 bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Subiendo...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Subir entregas</span>
                    </>
                  )}
                </button>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-red-900 font-montserrat mb-1">
                        Error
                      </h4>
                      <p className="text-sm text-red-700 font-montserrat">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Result */}
              {result && !showSuccessModal && (
                <div className="bg-[#67A43C]/10 border-l-4 border-[#67A43C] rounded-xl p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle className="w-5 h-5 text-[#67A43C] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-[#67A43C] font-montserrat mb-3">
                        {result.message || 'Proceso completado'}
                      </h4>
                      
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-600 font-montserrat mb-1">Creadas</p>
                          <p className="text-xl font-bold text-[#67A43C] font-montserrat">{result.created || 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-600 font-montserrat mb-1">Enviados</p>
                          <p className="text-xl font-bold text-[#67A43C] font-montserrat">{result.send_ok || 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-600 font-montserrat mb-1">Fallidos</p>
                          <p className="text-xl font-bold text-red-600 font-montserrat">{result.send_failed_count || 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-600 font-montserrat mb-1">Omitidos</p>
                          <p className="text-xl font-bold text-slate-600 font-montserrat">{result.skipped_count || 0}</p>
                        </div>
                      </div>

                      {/* Failed Messages */}
                      {Array.isArray(result.send_failed) && result.send_failed.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                            <h5 className="text-sm font-bold text-slate-900 font-montserrat">
                              Mensajes fallidos ({result.send_failed.length})
                            </h5>
                          </div>
                          {renderDetailsList(result.send_failed, ['row', 'phone', 'status'])}
                        </div>
                      )}

                      {/* Skipped Rows */}
                      {Array.isArray(result.skipped_rows) && result.skipped_rows.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-slate-600" />
                            <h5 className="text-sm font-bold text-slate-900 font-montserrat">
                              Filas omitidas ({result.skipped_rows.length})
                            </h5>
                          </div>
                          {renderDetailsList(result.skipped_rows, ['row'])}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-3 py-3 sm:px-6 sm:py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 sm:gap-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-white transition-all text-sm font-medium text-slate-700 disabled:opacity-50 font-montserrat"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-8 max-w-md w-full max-h-[calc(100dvh-1rem)] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PackageCheck className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-green-600 mb-2 font-montserrat">
                ¡Carga Completada!
              </h3>
              <p className="text-sm text-slate-600 mb-4 font-montserrat">
                Las entregas se han procesado exitosamente para <strong>{user?.username}</strong>.
              </p>
              {result && (
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="flex justify-around text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#67A43C] font-montserrat">{result.created || 0}</p>
                      <p className="text-xs text-slate-600 font-montserrat">Creadas</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#67A43C] font-montserrat">{result.send_ok || 0}</p>
                      <p className="text-xs text-slate-600 font-montserrat">Enviadas</p>
                    </div>
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  if (onSuccess) onSuccess();
                  onClose();
                }}
                className="w-full px-6 py-2.5 bg-[#67A43C] text-white rounded-xl font-medium hover:bg-[#67A43C]/90 transition-colors font-montserrat"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AddMasivoModal;
